import { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { MetricCard } from '@/components/reports/MetricCard';
import { DateRangeFilter } from '@/components/reports/DateRangeFilter';
import { ExportButton } from '@/components/reports/ExportButton';
import { WeeklyTrendChart } from '@/components/reports/WeeklyTrendChart';
import { FunnelChart } from '@/components/reports/FunnelChart';
import { useDashboardMetrics } from '@/hooks/useReports';
import type { FunnelStage } from '@recruitai/shared';

function getDefaultDates() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

export default function ReportsDashboardPage() {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const { data, isLoading, error } = useDashboardMetrics(startDate, endDate);

  const statusToFunnel = (dist: typeof data extends null ? never : NonNullable<typeof data>['candidatesByStatus']): FunnelStage[] => {
    const statuses = ['pending', 'analyzing', 'approved', 'rejected', 'interviewed', 'hired'] as const;
    const total = statuses.reduce((sum, s) => sum + (dist[s] || 0), 0);
    return statuses.map((s) => ({
      status: s,
      count: dist[s] || 0,
      percentage: total > 0 ? Math.round(((dist[s] || 0) / total) * 100) : 0,
    }));
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Relatórios</h1>
          <div className="flex items-center gap-3">
            <DateRangeFilter startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
            <ExportButton reportType="dashboard" startDate={startDate} endDate={endDate} />
          </div>
        </div>

        {isLoading && <p className="text-gray-500">Carregando métricas...</p>}
        {error && <p className="text-red-600">Erro ao carregar: {error.message}</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <MetricCard label="Vagas Abertas" value={data.totalOpenJobs} />
              <MetricCard label="Candidatos no Pipeline" value={data.totalCandidatesInPipeline} />
              <MetricCard label="Tempo Médio Contratação" value={data.averageHiringTime.toFixed(1)} suffix="dias" />
              <MetricCard label="Taxa de Aprovação" value={`${data.approvalRate.toFixed(1)}%`} />
              <MetricCard label="Total Entrevistas" value={data.totalInterviews} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <WeeklyTrendChart data={data.candidatesByWeek} title="Candidatos por Semana" />
              <FunnelChart data={statusToFunnel(data.candidatesByStatus)} />
            </div>

            <div className="bg-white rounded-lg border p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Top 5 Vagas</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Vaga</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Candidatos</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topJobs.map((job) => (
                    <tr key={job.jobId} className="border-t">
                      <td className="px-4 py-2 text-gray-900">{job.title}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{job.candidateCount}</td>
                    </tr>
                  ))}
                  {data.topJobs.length === 0 && (
                    <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-500">Nenhuma vaga encontrada</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
